import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { VisibilityProvider, useDashboardVisibility } from "./lib/visibility";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LeadsPerformancePage from "./pages/LeadsPerformancePage";
import ZonasHotPage from "./pages/ZonasHotPage";
import PredictivePage from "./pages/PredictivePage";
import DataEntryPage from "./pages/DataEntryPage";
import AdminIntegrationsPage from "./pages/AdminIntegrationsPage";
import ScorecardPage from "./pages/ScorecardPage";
import ScorecardMethodologyPage from "./pages/ScorecardMethodologyPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import TrainingPage from "./pages/TrainingPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import FniPage from "./pages/FniPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SalesPerformancePage from "./pages/SalesPerformancePage";
import AppShell from "./components/AppShell";
import SystemUpdateOverlay from "./components/SystemUpdateOverlay";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen grid place-items-center"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}></div></div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

// Gated dashboards: admins always pass; non-admins are redirected to the
// first available dashboard when their target is hidden. If every dashboard
// is hidden we render a friendly "no access" message instead of looping.
function GatedDashboard({ dashboardKey, children }) {
  const { flags, isAdmin, loading } = useDashboardVisibility();
  if (loading) {
    return <div className="min-h-[50vh] grid place-items-center"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}></div></div>;
  }
  if (isAdmin) return children;
  if (flags[dashboardKey] !== false) return children;
  // Find a fallback route the user CAN see.
  const fallback = (
    flags.sales_performance !== false ? "/sales-performance"
    : flags.crm_overview !== false ? "/crm-overview"
    : flags.leads_performance !== false ? "/leads-performance"
    : flags.predictive !== false ? "/predictive"
    : null
  );
  if (fallback === null) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <div className="surface p-8 text-center max-w-md" data-testid="no-dashboard-access">
          <h2 className="font-display font-extrabold text-xl mb-2">Sin dashboards habilitados</h2>
          <p className="text-sm text-gray-500 font-semibold">
            Tu administrador no ha habilitado ningún dashboard para tu usuario.
            Contacta al administrador para solicitar acceso.
          </p>
        </div>
      </div>
    );
  }
  return <Navigate to={fallback} replace />;
}

export default function App() {
  return (
    <div className="App">
      <SystemUpdateOverlay />
      <BrowserRouter>
        <AuthProvider>
          <VisibilityProvider>
          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={5000}
            toastOptions={{
              style: { fontFamily: "'Manrope', sans-serif", fontWeight: 600 },
            }}
          />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/" element={<Protected><GatedDashboard dashboardKey="sales_performance"><Navigate to="/sales-performance" replace /></GatedDashboard></Protected>} />
            <Route path="/crm-overview" element={<Protected><GatedDashboard dashboardKey="crm_overview"><DashboardPage /></GatedDashboard></Protected>} />
            <Route path="/leads-performance" element={<Protected><GatedDashboard dashboardKey="leads_performance"><LeadsPerformancePage /></GatedDashboard></Protected>} />
            <Route path="/predictive" element={<Protected><GatedDashboard dashboardKey="predictive"><PredictivePage /></GatedDashboard></Protected>} />
            <Route path="/scorecard" element={<Protected><GatedDashboard dashboardKey="scorecard"><ScorecardPage /></GatedDashboard></Protected>} />
            <Route path="/zonas-hot" element={<Protected><GatedDashboard dashboardKey="zonas_hot"><ZonasHotPage /></GatedDashboard></Protected>} />
            <Route path="/scorecard/metodologia" element={<Protected><GatedDashboard dashboardKey="scorecard"><ScorecardMethodologyPage /></GatedDashboard></Protected>} />
            <Route path="/sales-performance" element={<Protected><GatedDashboard dashboardKey="sales_performance"><SalesPerformancePage /></GatedDashboard></Protected>} />
            <Route path="/datos" element={<Protected><DataEntryPage /></Protected>} />
            <Route path="/admin/integrations" element={<Protected><AdminIntegrationsPage /></Protected>} />
            <Route path="/admin/users" element={<Protected><AdminUsersPage /></Protected>} />
            <Route path="/fni" element={<Protected><FniPage /></Protected>} />
            <Route path="/entrenamiento" element={<Protected><TrainingPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </VisibilityProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
